import {
  AccessTime,
  CancelOutlined,
  ChatBubble,
  CheckCircle,
  Checkroom,
  CrisisAlert,
  Domain,
  Groups,
  IosShare,
  ListAlt,
  LocalAtm,
  SearchOffOutlined as SearchOffIcon,
  Warning,
} from "@mui/icons-material";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { AdminEmptyState } from "@/components/common/AdminEmptyState";
import { RecordFields, RecordList } from "@/components/common/RecordList";
import AnalyticsCard from "@/components/common/AnalyticsCard";
import CraveScoreCard from "@/components/common/CraveScoreCard/CraveScoreCard";
import { DataFreshnessFooter } from "@/components/common/DataFreshnessFooter";
import { LongRunningDataLoader } from "@/components/common/LongRunningDataLoader";
import { TableSkeletonRows } from "@/components/common/TableSkeletonRows";
import type { FilterMenuOption } from "@/components/common/FilterMenuButton";
import MuiAutocompleteMultiSelect from "@/components/common/MuiAutocompleteMultiSelect";
import {
  ComparisonPageHeaderShell,
  InsightsHeaderSelectorField,
  InsightsHeaderSelectorRow,
} from "@/components/common/ReportingPageHeaderShell";
import ReportingWindowSelector from "@/components/common/ReportingWindowSelector";
import { SortSelectorControl } from "@/components/common/SortSelectorControl";
import { tableInCardSx } from "@/components/common/unifiedTableSx";
import StoreMultiSelect from "@/components/common/StoreMultiSelect";
import { type DeviceType } from "@/components/filters/DeviceTypeFilter";
import { useCanceledRequests } from "@/hooks/useCanceledRequests";
import { useCantFindRequests } from "@/hooks/useCantFindRequests";
import { useCustomerSatisfaction } from "@/hooks/useCustomerSatisfaction";
import { useDateRangeBadge } from "@/hooks/useDateRangeBadge";
import { useDwellTime } from "@/hooks/useDwellTime";
import { useFulfilledRequests } from "@/hooks/useFulfilledRequests";
import { useMissedRequests } from "@/hooks/useMissedRequests";
import { useNamedShoppers } from "@/hooks/useNamedShoppers";
import { useLocalizedTablePagination } from "@/hooks/useLocalizedTablePagination";
import { useResponseTime } from "@/hooks/useResponseTime";
import {
  type StorePerformanceMetric,
  useStorePerformanceTrends,
} from "@/hooks/useStorePerformanceTrends";
import { useTotalRequests } from "@/hooks/useTotalRequests";
import { useTriedOnValue } from "@/hooks/useTriedOnValue";
import { useUnitsPerSession } from "@/hooks/useUnitsPerSession";
import i18n from "@/i18n/i18n";
import { ComparisonBreakdownPanel } from "@/pages/store-insights/shared/ComparisonBreakdownPanel";
import { STORE_INSIGHTS_MULTI_STORE_OVERVIEW_SELECTION_SESSION_KEY } from "@/pages/store-insights/shared/storeInsightsSelectionSession";
import { useStoreInsightsDeviceTypeFilter } from "@/pages/store-insights/shared/useStoreInsightsDeviceTypeFilter";
import { useStoreInsightsMultiStoreSelection } from "@/pages/store-insights/shared/useStoreInsightsMultiStoreSelection";
import { useStoreInsightsSharedDateWindow } from "@/pages/store-insights/shared/useStoreInsightsSharedDateWindow";
import { shouldShowInsightsTrends } from "@/pages/store-insights/shared/insightsFeatureGates";
import { useStoreInsightsTrendComparison } from "@/pages/store-insights/shared/useStoreInsightsTrendComparison";
import { AnalyticsServiceV2 } from "@/services/features/AnalyticsServiceV2";
import { CustomerService } from "@/services/features/CustomerService";
import { Store } from "@/services/features/StoreService";
import { DateRange, storePerformanceService } from "@/services/StorePerformanceService";
import { calculateCraveScore, getCraveScoreConfigForDevice } from "@/utils/craveScore";
import { formatTrendChange } from "@/utils/analyticsTrend";
import {
  CurrencyCode,
  formatCurrencyCSV,
  formatCurrencyWithNoDecimals,
  getStoreCurrencyIdentifiers,
} from "@/utils/currency";
import {
  formatCompactNumber,
  formatDecimal,
  formatInteger,
  formatPercentFromWhole,
} from "@/utils/number";
import {
  formatStoreDateRange,
  formatStoreDateTimeFileStamp,
  getBrowserTimezone,
  getInclusiveStoreDayCount,
} from "@/utils/storeTime";
import { formatDurationShort, formatMillisecondsShort } from "@/utils/time";
import { formatStoreSelectorLabel } from "@/utils/store";

const multiStoreOverviewTrendMetrics: StorePerformanceMetric[] = [
  "sessions",
  "unitsPerSession",
  "missedRequests",
  "responseTime",
  "totalRequests",
  "fulfilledRequests",
  "cantFinds",
  "cancelledRequests",
  "dwellTime",
  "namedShoppers",
  "experienceRating",
];

export const MultiStoreOverview: React.FC = () => {
  const { t } = useTranslation();
  const browserTimezone = getBrowserTimezone();
  const navigate = useNavigate();
  const [overviewExpanded, setOverviewExpanded] = useState<boolean>(false);
  const collapsibleRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<number | "none">(590);
  const measureOverviewHeight = useCallback(() => {
    const el = collapsibleRef.current;
    if (!el) {
      return null;
    }

    return el.scrollHeight;
  }, []);
  const { period, customRange, currentDateRange, onPeriodChange, onCustomRangeChange } =
    useStoreInsightsSharedDateWindow({
      defaultPeriod: "yesterday",
    });
  const {
    comparisonWindow,
    setComparisonWindow,
    anchorComparisonToWeekdays,
    setAnchorComparisonToWeekdays,
  } = useStoreInsightsTrendComparison(period, currentDateRange);
  const {
    user,
    isCraveUser,
    customers,
    loading,
    selectionLoading,
    selectedCustomerIds,
    selectedGroups,
    selectedStores,
    groups,
    groupCounts,
    filteredStores,
    onCustomerChange,
    onGroupsChange,
    onStoresChange,
  } = useStoreInsightsMultiStoreSelection({
    sessionKey: STORE_INSIGHTS_MULTI_STORE_OVERVIEW_SELECTION_SESSION_KEY,
    includeUngroupedGroups: true,
  });
  const { deviceType, onDeviceTypeChange } = useStoreInsightsDeviceTypeFilter({
    selectionSession: {
      kind: "multi",
      key: STORE_INSIGHTS_MULTI_STORE_OVERVIEW_SELECTION_SESSION_KEY,
    },
    selectionReady: !loading && !selectionLoading,
    preserveAllInUrl: true,
  });
  const supportsRequestInsights = deviceType !== "RFID_CENTRAL";
  const showTrends = true;

  // Format YYYY-MM-DD for URL
  const fmtYMD = (d: Date) => storePerformanceService.formatDate(d, browserTimezone);

  const handleNavigateToReport = (reportType: "sessions" | "missed" | "cant-find") => {
    const range = currentDateRange;
    const params = new URLSearchParams();

    if (period === "custom") {
      params.set("period", "custom");
      params.set("start", fmtYMD(range.start));
      params.set("end", fmtYMD(range.end));
    } else {
      params.set("period", period);
    }

    if (selectedStores && selectedStores.length) {
      const ids = selectedStores
        .map((s) => s.id)
        .sort((a, b) => a - b)
        .join(",");
      params.set("storeIds", ids);
    }

    if (selectedCustomerIds.length) {
      params.set("customerId", selectedCustomerIds.join(","));
    }

    if (selectedGroups.length) {
      params.set("group", selectedGroups.join(","));
    }

    if (deviceType && deviceType !== "all") {
      params.set("deviceType", deviceType);
    }

    navigate(`/reports/${reportType}?${params.toString()}`);
  };

  const { freshnessFooterIcon, freshnessFooterText, freshnessFooterTooltip } = useDateRangeBadge({
    period,
  });

  const selectedIds = useMemo(() => selectedStores.map((s) => s.id), [selectedStores]);

  const unitsPerSessionData = useUnitsPerSession(
    selectedStores.length ? selectedStores : null,
    currentDateRange,
    deviceType,
  );
  const missedRequestsData = useMissedRequests(
    selectedStores.length ? selectedStores : null,
    currentDateRange,
    deviceType,
  );
  const fulfilledRequestsData = useFulfilledRequests(
    selectedStores.length ? selectedStores : null,
    currentDateRange,
    deviceType,
  );
  const cantFindRequestsData = useCantFindRequests(
    selectedStores.length ? selectedStores : null,
    currentDateRange,
    deviceType,
  );
  const responseTimeData = useResponseTime(
    selectedIds.length ? selectedIds : null,
    currentDateRange,
    deviceType,
  );
  const customerSatisfactionData = useCustomerSatisfaction(
    selectedIds.length ? selectedIds : null,
    currentDateRange,
  );
  const totalRequestsData = useTotalRequests(
    selectedStores.length ? selectedStores : null,
    currentDateRange,
    deviceType,
  );
  const canceledRequestsData = useCanceledRequests(
    selectedStores.length ? selectedStores : null,
    currentDateRange,
    deviceType,
  );
  const dwellTimeData = useDwellTime(
    selectedIds.length ? selectedIds : null,
    currentDateRange,
    deviceType,
  );
  const namedShoppersData = useNamedShoppers(
    selectedIds.length ? selectedIds : null,
    currentDateRange,
    deviceType,
  );
  const triedOnValueData = useTriedOnValue(
    selectedStores.length ? selectedStores : null,
    currentDateRange,
    deviceType,
  );
  const titleLoading = Boolean(
    loading || missedRequestsData.loading || cantFindRequestsData.percentage.loading,
  );

  const trendCurrentValues = useMemo<Partial<Record<StorePerformanceMetric, number | null>>>(
    () => ({
      sessions: triedOnValueData.rawValue,
      unitsPerSession: unitsPerSessionData.rawValue,
      missedRequests: missedRequestsData.rawValue,
      responseTime: responseTimeData.rawValue,
      totalRequests: totalRequestsData.rawValue,
      fulfilledRequests: fulfilledRequestsData.rawValue,
      cantFinds: cantFindRequestsData.percentage.rawValue,
      cancelledRequests: canceledRequestsData.rawValue,
      dwellTime: dwellTimeData.rawValue,
      namedShoppers: namedShoppersData.trendRawValue,
      experienceRating: customerSatisfactionData.rawValue,
    }),
    [
      canceledRequestsData.rawValue,
      cantFindRequestsData.percentage.rawValue,
      customerSatisfactionData.rawValue,
      dwellTimeData.rawValue,
      fulfilledRequestsData.rawValue,
      missedRequestsData.rawValue,
      namedShoppersData.trendRawValue,
      responseTimeData.rawValue,
      totalRequestsData.rawValue,
      triedOnValueData.rawValue,
      unitsPerSessionData.rawValue,
    ],
  );
  const { comparisonRange, trends, comparisonProgress } = useStorePerformanceTrends({
    enabled: Boolean(
      selectedIds.length > 0 &&
      !loading &&
      !missedRequestsData.loading &&
      !cantFindRequestsData.percentage.loading &&
      !canceledRequestsData.loading,
    ),
    storeIds: selectedIds,
    comparisonWindow,
    anchorComparisonToWeekdays:
      (period === "last_4_weeks" || period === "custom") &&
      (comparisonWindow === "month_before" || comparisonWindow === "year_before")
        ? anchorComparisonToWeekdays
        : true,
    currentRange: currentDateRange,
    deviceType,
    metrics: multiStoreOverviewTrendMetrics,
    currentValues: trendCurrentValues,
  });
  const formatPreviousTrendValue = useCallback(
    (metric: StorePerformanceMetric, value: number, locale: string) => {
      switch (metric) {
        case "sessions":
          return formatInteger(value, locale);
        case "unitsPerSession":
          return formatDecimal(value, locale, { maximumFractionDigits: 1 });
        case "missedRequests":
          return formatPercentFromWhole(value, locale);
        case "responseTime":
          return formatDurationShort(value, locale);
        case "totalRequests":
          return formatInteger(value, locale);
        case "fulfilledRequests":
          return formatPercentFromWhole(value, locale);
        case "cantFinds":
          return formatPercentFromWhole(value, locale);
        case "cancelledRequests":
          return formatPercentFromWhole(value, locale);
        case "dwellTime":
          return formatDurationShort(value, locale);
        case "namedShoppers":
          return formatPercentFromWhole(value, locale);
        case "experienceRating":
          return formatDecimal(value, locale, { maximumFractionDigits: 1 });
        case "revenue":
        case "conversionRate":
          return "";
      }
    },
    [],
  );
  const trendProps = useCallback(
    (metric: StorePerformanceMetric) => {
      if (!showTrends) return undefined;

      const trend = trends[metric];
      const locale = i18n.resolvedLanguage || i18n.language;
      const range = formatStoreDateRange(
        comparisonRange.start,
        comparisonRange.end,
        browserTimezone,
        locale,
        {
          weekday: "short",
          month: "short",
          day: "numeric",
        },
      );
      const dateRange =
        range.startDate === range.endDate
          ? range.startDate
          : `${range.startDate} – ${range.endDate}`;
      return {
        ...trend,
        text: formatTrendChange(
          trend,
          (value) => formatPreviousTrendValue(metric, value, locale),
          (value) => formatPercentFromWhole(value, locale),
          t("common.analytics.trends.noChange"),
        ),
        tooltip:
          trend.status === "ready"
            ? t("common.analytics.trends.tooltip", {
                dateRange,
                previousValue: [
                  formatPreviousTrendValue(metric, trend.previousValue, locale),
                  comparisonProgress[metric] &&
                    t("common.analytics.requestsProgress", comparisonProgress[metric]),
                ]
                  .filter(Boolean)
                  .join(" • "),
              })
            : undefined,
      };
    },
    [
      browserTimezone,
      comparisonRange.end,
      comparisonRange.start,
      formatPreviousTrendValue,
      showTrends,
      t,
      trends,
      comparisonProgress,
    ],
  );

  useEffect(() => {
    if (!overviewExpanded) {
      setMaxHeight(590);
    }
  }, [overviewExpanded]);

  // Dynamic sessions label based on device type
  const sessionsLabel = useMemo(() => {
    switch (deviceType) {
      case "RFID_CENTRAL":
        return t("storeComparison.fields.centralSessions");
      case "IN_AISLE":
        return t("storeComparison.fields.inAisleSessions");
      case "FITTING_ROOM":
        return t("storeComparison.fields.fittingRoomSessions");
      case "MOBILE_PICKUP":
        return t("storeComparison.fields.mobileSessions");
      case "all":
      default:
        return t("storeComparison.fields.sessions");
    }
  }, [deviceType, t]);

  const deviceTypeLabel = useMemo(() => {
    switch (deviceType) {
      case "RFID_CENTRAL":
        return t("filters.deviceTypes.central");
      case "IN_AISLE":
        return t("filters.deviceTypes.inAisle");
      case "MOBILE_PICKUP":
        return t("filters.deviceTypes.mobile");
      case "FITTING_ROOM":
        return t("filters.deviceTypes.fittingRoom");
      case "all":
      default:
        return t("filters.deviceTypes.all");
    }
  }, [deviceType, t]);

  // Build Crave Score inputs for the multi-store aggregate (uses existing metric hooks)
  const craveInputs = useMemo(() => {
    return [
      {
        id: "sessions",
        label: sessionsLabel,
        rawValue: triedOnValueData.rawValue,
      },
      {
        id: "ups",
        label: t("storeComparison.fields.unitsPerSession"),
        rawValue: unitsPerSessionData.rawValue,
      },
      {
        id: "missedPct",
        label: t("storeComparison.fields.missedRequestsPercent"),
        rawValue: missedRequestsData.rawValue,
      },
      {
        id: "avgResponseSec",
        label: t("storeComparison.fields.averageResponseTimeSeconds"),
        rawValue: responseTimeData.rawValue,
      },
      {
        id: "fulfilledPct",
        label: t("storeComparison.fields.fulfilledRequestsPercent"),
        rawValue: fulfilledRequestsData.rawValue,
      },
      {
        id: "cantFindPct",
        label: t("storeComparison.fields.cantFindsPercent"),
        rawValue: cantFindRequestsData.percentage.rawValue,
      },
      {
        id: "canceledPct",
        label: t("storeComparison.fields.cancelledRequestsPercent"),
        rawValue: canceledRequestsData.rawValue,
      },
      {
        id: "totalRequests",
        label: t("storeComparison.fields.totalRequests"),
        rawValue: totalRequestsData.rawValue,
      },
      {
        id: "dwellSec",
        label: t("storeComparison.fields.dwellTimeSeconds"),
        rawValue: dwellTimeData.rawValue,
      },
      {
        id: "namedShoppersPct",
        label: t("storeComparison.fields.namedShoppersPercent"),
        rawValue: namedShoppersData.rawValue,
      },
      {
        id: "satisfaction",
        label: t("storeComparison.fields.experienceRating"),
        rawValue: customerSatisfactionData.rawValue,
      },
    ];
  }, [
    t,
    sessionsLabel,
    triedOnValueData.rawValue,
    unitsPerSessionData.rawValue,
    missedRequestsData.rawValue,
    responseTimeData.rawValue,
    fulfilledRequestsData.rawValue,
    cantFindRequestsData.percentage.rawValue,
    canceledRequestsData.rawValue,
    totalRequestsData.rawValue,
    dwellTimeData.rawValue,
    namedShoppersData.rawValue,
    customerSatisfactionData.rawValue,
  ]);

  // Helper: safely update search params without dropping existing keys
  return (
    <div className="store-comparison-page">
      <ComparisonPageHeaderShell
        title={t("storeComparison.title")}
        gradientTitle
        icon={<Domain />}
        loading={titleLoading}
        headerControls={
          <InsightsHeaderSelectorRow className="store-comparison-print-hide" sx={{ gap: 2 }}>
            {isCraveUser && (
              <InsightsHeaderSelectorField sx={{ width: { xs: "100%", md: 160, lg: 300 } }}>
                <StoreMultiSelect
                  className="w-full"
                  minDropdownWidth={300}
                  dropdownAlign="right"
                  options={customers}
                  value={customers.filter((c) => selectedCustomerIds.includes(c.id))}
                  onChange={(next) => {
                    onCustomerChange(next.map((c) => (c as any).id as number));
                  }}
                  label={t("storeComparison.fields.customers")}
                  loading={loading}
                  getOptionLabel={(c) => (c as any).name as string}
                  getOptionKey={(c) => (c as any).id as number}
                  emptyText={t("storeComparison.empty.noCustomers")}
                />
              </InsightsHeaderSelectorField>
            )}

            <InsightsHeaderSelectorField sx={{ width: { xs: "100%", md: 160, lg: 300 } }}>
              <StoreMultiSelect
                className="w-full"
                minDropdownWidth={300}
                dropdownAlign="right"
                rowHeight="default"
                searchThreshold={0}
                options={groups}
                value={selectedGroups}
                currentUserRole={user?.role}
                isDemo={(g) => g.toLowerCase().includes("demo")}
                onChange={onGroupsChange}
                label={t("storeComparison.fields.groups")}
                loading={loading}
                getOptionLabel={(g) => (g === "" ? t("storeComparison.option.ungrouped") : g)}
                getOptionKey={(g) => g}
                getOptionSecondary={(g) => {
                  const n = groupCounts[g] ?? 0;
                  return t("storeComparison.groupStoresCount", { count: n });
                }}
                emptyText={t("storeComparison.empty.noGroups")}
              />
            </InsightsHeaderSelectorField>

            <InsightsHeaderSelectorField sx={{ width: { xs: "100%", md: 180, lg: 360 } }}>
              <MuiAutocompleteMultiSelect
                className="w-full font-bold"
                minDropdownWidth={440}
                dropdownAlign="right"
                multiSelect
                options={filteredStores}
                value={selectedStores}
                currentUserRole={user?.role}
                isDemo={(s) => {
                  const name = String((s as any).name || "").toLowerCase();
                  const group = String((s as any).group || "").toLowerCase();
                  return name.includes("demo") || group.includes("demo");
                }}
                onChange={onStoresChange}
                label={t("storeComparison.fields.stores")}
                disabled={loading}
                loading={loading}
                getOptionLabel={(s) => formatStoreSelectorLabel(s, t, user?.role, "id-name")}
                getOptionKey={(s) => (s as any).id as number}
                placeholder={t("storeComparison.placeholders.searchStores")}
                emptyText={t("storeComparison.empty.noStoresFound")}
              />
            </InsightsHeaderSelectorField>
          </InsightsHeaderSelectorRow>
        }
        secondaryControls={
          <div className="flex flex-col gap-4 mb-4">
            <div className="w-full store-comparison-print-hide">
              <ReportingWindowSelector
                className="store-comparison-date-filter"
                value={period}
                onChange={onPeriodChange}
                showRangeText
                dateRange={currentDateRange}
                customRange={customRange}
                onCustomRangeChange={onCustomRangeChange}
                showDeviceFilter
                showTrendSelector
                trendPeriod={comparisonWindow}
                onTrendPeriodChange={setComparisonWindow}
                trendComparisonRange={comparisonRange}
                anchorTrendComparisonToWeekdays={anchorComparisonToWeekdays}
                onAnchorTrendComparisonToWeekdaysChange={setAnchorComparisonToWeekdays}
                selectedDeviceType={deviceType}
                onDeviceTypeChange={onDeviceTypeChange}
              />
            </div>
            <div className="hidden w-full print:block">
              <ReportingWindowSelector
                className="store-comparison-date-filter"
                value={period}
                onChange={onPeriodChange}
                showRangeText
                dateRange={currentDateRange}
                customRange={customRange}
                onCustomRangeChange={onCustomRangeChange}
                trendPeriod={comparisonWindow}
                trendComparisonRange={comparisonRange}
              />
            </div>
          </div>
        }
        printMeta={
          <div className="store-comparison-print-only store-comparison-print-meta">
            <span>
              {selectedStores.length === 1
                ? selectedStores[0]?.name
                : t("groupComparison.groupComparison.selectedStores", {
                    count: selectedStores.length,
                  })}
            </span>
            <span>{deviceTypeLabel}</span>
          </div>
        }
      />
      <Box sx={{ p: { xs: 2, md: 2.5 }, pt: 0 }}>
        {/* Collapsible Overview: Key Levers, Requests, Sessions */}
        <Box sx={{ position: "relative", mt: 3 }}>
          <div
            id="overview-collapsible"
            ref={collapsibleRef}
            className="store-comparison-overview-collapsible overflow-hidden transition-[max-height] duration-700 ease-in-out will-change-[max-height]"
            style={{ maxHeight }}
            aria-expanded={overviewExpanded}
            onTransitionEnd={(event) => {
              if (
                event.target !== event.currentTarget ||
                event.propertyName !== "max-height" ||
                !overviewExpanded
              ) {
                return;
              }

              setMaxHeight("none");
            }}
          >
            <div>
              {/* Crave Score component */}
              <Box sx={{ mb: 4 }}>
                <CraveScoreCard
                  inputs={craveInputs}
                  config={getCraveScoreConfigForDevice(deviceType)}
                />
              </Box>
              <Typography component="h2" variant="h6" sx={{ mb: 2, fontSize: 20, fontWeight: 600 }}>
                {t("storeComparison.sections.keyPerformanceLevers")}
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "minmax(0, 1fr)",
                    lg: "repeat(2, minmax(0, 1fr))",
                    xl: supportsRequestInsights
                      ? "repeat(4, minmax(0, 1fr))"
                      : "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 2,
                }}
              >
                <div className="min-w-0">
                  <AnalyticsCard
                    value={triedOnValueData.value}
                    previousText={triedOnValueData.previousValue}
                    title={sessionsLabel}
                    isPositive={triedOnValueData.isPositive}
                    subtitle={triedOnValueData.subtitle}
                    icon={<LocalAtm />}
                    pillText={triedOnValueData.pillText}
                    trend={trendProps("sessions")}
                    subtitleLoading={trends.sessions.status === "loading"}
                    onClick={() => handleNavigateToReport("sessions")}
                    countUp
                    disableFade
                  />
                </div>
                <div className="min-w-0">
                  <AnalyticsCard
                    value={unitsPerSessionData.value}
                    previousText={unitsPerSessionData.previousValue}
                    title={t("storeComparison.fields.unitsPerSession")}
                    isPositive={unitsPerSessionData.isPositive}
                    subtitle={unitsPerSessionData.subtitle}
                    icon={<Checkroom />}
                    pillText={unitsPerSessionData.pillText}
                    trend={trendProps("unitsPerSession")}
                    subtitleLoading={trends.unitsPerSession.status === "loading"}
                  />
                </div>
                {supportsRequestInsights && (
                  <>
                    <div className="min-w-0">
                      <AnalyticsCard
                        value={missedRequestsData.value}
                        previousText={missedRequestsData.previousValue}
                        title={t("storeComparison.fields.missedRequests")}
                        isPositive={missedRequestsData.isPositive}
                        subtitle={missedRequestsData.subtitle}
                        icon={<CrisisAlert />}
                        pillText={missedRequestsData.pillText}
                        trend={trendProps("missedRequests")}
                        subtitleLoading={trends.missedRequests.status === "loading"}
                        onClick={() => handleNavigateToReport("missed")}
                      />
                    </div>
                    <div className="min-w-0">
                      <AnalyticsCard
                        value={responseTimeData.value}
                        previousText={responseTimeData.previousValue}
                        title={t("storeComparison.fields.averageResponseTime")}
                        isPositive={responseTimeData.isPositive}
                        subtitle={responseTimeData.subtitle}
                        icon={<AccessTime />}
                        pillText={responseTimeData.pillText}
                        trend={trendProps("responseTime")}
                      />
                    </div>
                  </>
                )}
              </Box>
            </div>

            {supportsRequestInsights && (
              <Box sx={{ mt: 4 }}>
                <Typography
                  component="h2"
                  variant="h6"
                  sx={{ mb: 2, fontSize: 20, fontWeight: 600 }}
                >
                  {t("storeComparison.sections.requestsBreakdown")}
                </Typography>
                <div className="flex flex-wrap gap-4 mb-4">
                  <div className="flex-1 min-w-[300px]">
                    <AnalyticsCard
                      value={totalRequestsData.value}
                      previousText={totalRequestsData.previousValue}
                      title={t("storeComparison.fields.totalRequests")}
                      isPositive={totalRequestsData.isPositive}
                      subtitle={totalRequestsData.subtitle}
                      icon={<ListAlt />}
                      pillText={null}
                      trend={trendProps("totalRequests")}
                      subtitleLoading={trends.totalRequests.status === "loading"}
                      showGradient={true}
                    />
                  </div>
                  <div className="flex-1 min-w-[300px]">
                    <AnalyticsCard
                      value={fulfilledRequestsData.value}
                      previousText={fulfilledRequestsData.previousValue}
                      title={t("storeComparison.fields.fulfilledRequests")}
                      isPositive={fulfilledRequestsData.isPositive}
                      subtitle={fulfilledRequestsData.subtitle}
                      icon={<CheckCircle />}
                      pillText={fulfilledRequestsData.pillText}
                      trend={trendProps("fulfilledRequests")}
                      subtitleLoading={trends.fulfilledRequests.status === "loading"}
                      showGradient={true}
                    />
                  </div>
                </div>
                <div className="flex flex-wrap gap-4">
                  <div className="flex-1 min-w-[300px]">
                    <AnalyticsCard
                      value={missedRequestsData.value}
                      previousText={missedRequestsData.previousValue}
                      title={t("storeComparison.fields.missedRequests")}
                      isPositive={missedRequestsData.isPositive}
                      subtitle={missedRequestsData.subtitle}
                      icon={<CrisisAlert />}
                      pillText={missedRequestsData.pillText}
                      trend={trendProps("missedRequests")}
                      subtitleLoading={trends.missedRequests.status === "loading"}
                      showGradient={true}
                      onClick={() => handleNavigateToReport("missed")}
                    />
                  </div>
                  <div className="flex-1 min-w-[300px]">
                    <AnalyticsCard
                      value={cantFindRequestsData.percentage.value}
                      previousText={cantFindRequestsData.percentage.previousValue}
                      title={t("storeComparison.fields.cantFinds")}
                      isPositive={cantFindRequestsData.percentage.isPositive}
                      subtitle={cantFindRequestsData.percentage.subtitle}
                      icon={<Warning />}
                      pillText={cantFindRequestsData.percentage.pillText}
                      trend={trendProps("cantFinds")}
                      subtitleLoading={trends.cantFinds.status === "loading"}
                      showGradient={true}
                      onClick={() => handleNavigateToReport("cant-find")}
                    />
                  </div>
                  <div className="flex-1 min-w-[300px]">
                    <AnalyticsCard
                      value={canceledRequestsData.value}
                      previousText={canceledRequestsData.previousValue}
                      title={t("storeComparison.fields.cancelledRequests")}
                      isPositive={canceledRequestsData.isPositive}
                      subtitle={canceledRequestsData.subtitle}
                      icon={<CancelOutlined />}
                      pillText={canceledRequestsData.pillText}
                      trend={trendProps("cancelledRequests")}
                      subtitleLoading={trends.cancelledRequests.status === "loading"}
                      showGradient={true}
                    />
                  </div>
                </div>
              </Box>
            )}

            <Box sx={{ mt: 4, mb: 4 }}>
              <Typography component="h2" variant="h6" sx={{ mb: 2, fontSize: 20, fontWeight: 600 }}>
                {t("storeComparison.sections.sessionBreakdown")}
              </Typography>
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[300px]">
                  <AnalyticsCard
                    value={dwellTimeData.value}
                    previousText={dwellTimeData.previousValue}
                    title={t("storeComparison.fields.dwellTime")}
                    isPositive={dwellTimeData.isPositive}
                    subtitle={dwellTimeData.subtitle}
                    icon={<AccessTime />}
                    pillText={dwellTimeData.pillText}
                    trend={trendProps("dwellTime")}
                    showGradient={true}
                  />
                </div>
                <div className="flex-1 min-w-[300px]">
                  <AnalyticsCard
                    value={namedShoppersData.value}
                    previousText={namedShoppersData.previousValue}
                    title={t("storeComparison.fields.namedShoppers")}
                    isPositive={namedShoppersData.isPositive}
                    subtitle={namedShoppersData.subtitle}
                    icon={<Groups />}
                    pillText={namedShoppersData.pillText}
                    pillTextAsChip
                    trend={trendProps("namedShoppers")}
                    subtitleLoading={trends.namedShoppers.status === "loading"}
                    showGradient={true}
                  />
                </div>
                <div className="flex-1 min-w-[300px]">
                  <AnalyticsCard
                    value={(() => {
                      const val = customerSatisfactionData.value;
                      if (!val || val === "--" || typeof val !== "string") return val;
                      const rating = parseFloat(val);
                      if (isNaN(rating)) return val;
                      const clamped = Math.max(0, Math.min(5, rating));
                      const percent = (clamped / 5) * 100;
                      return (
                        <span
                          className="inline-flex items-center gap-1"
                          aria-label={t("storeComparison.ratingOutOfFive", {
                            rating,
                          })}
                          title={t("storeComparison.ratingTooltip", { rating })}
                        >
                          <span>{val}</span>
                          <span
                            className="relative inline-block text-2xl leading-none align-middle"
                            aria-hidden="true"
                          >
                            <span className="text-gray-300">★★★★★</span>
                            <span
                              className="absolute left-0 rtl:left-auto rtl:right-0 top-0 overflow-hidden text-black"
                              style={{ width: `${percent}%` }}
                            >
                              ★★★★★
                            </span>
                          </span>
                        </span>
                      );
                    })()}
                    previousText={customerSatisfactionData.previousValue}
                    title={t("storeComparison.fields.experienceRating")}
                    isPositive={customerSatisfactionData.isPositive}
                    subtitle={customerSatisfactionData.subtitle}
                    icon={<ChatBubble />}
                    pillText={customerSatisfactionData.pillText}
                    trend={trendProps("experienceRating")}
                    subtitleLoading={trends.experienceRating.status === "loading"}
                    showGradient={true}
                  />
                </div>
              </div>
            </Box>
          </div>

          <div
            className={`store-comparison-overview-fade pointer-events-none absolute bottom-12 left-0 right-0 h-16 transition-opacity duration-300 ${
              overviewExpanded ? "opacity-0" : "opacity-100"
            }`}
            style={{
              backgroundImage: "linear-gradient(to top, #f7faff 0%, #f7faff 10%, transparent 100%)",
            }}
          />

          <div className="store-comparison-overview-toggle flex justify-center mt-4">
            <button
              type="button"
              onClick={() => {
                const el = collapsibleRef.current;
                if (!overviewExpanded) {
                  // expanding: measure target height
                  if (el) {
                    requestAnimationFrame(() => {
                      const nextHeight = measureOverviewHeight();
                      if (nextHeight !== null) {
                        setMaxHeight(nextHeight);
                      }
                    });
                  }
                  setOverviewExpanded(true);
                } else {
                  // collapsing: start from current height, then animate to collapsed height
                  if (el) {
                    const h = measureOverviewHeight();
                    if (h !== null) {
                      setMaxHeight(h);
                    }
                    requestAnimationFrame(() => setMaxHeight(590));
                  } else {
                    setMaxHeight(590);
                  }
                  setOverviewExpanded(false);
                }
              }}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white text-primary hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary aria-selected:ring-primary"
              aria-expanded={overviewExpanded}
              aria-controls="overview-collapsible"
            >
              {overviewExpanded
                ? t("storeComparison.actions.showLess")
                : t("storeComparison.actions.showAll")}
              <svg
                className={`h-4 w-4 transition-transform duration-300 ${overviewExpanded ? "rotate-180" : ""}`}
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M6 8l4 4 4-4" />
              </svg>
            </button>
          </div>
        </Box>

        {/* Store metrics breakdown table */}
        <StoreMetricsBreakdownTable
          selectedStores={selectedStores}
          dateRange={currentDateRange}
          deviceType={deviceType}
        />
      </Box>
      <DataFreshnessFooter
        icon={freshnessFooterIcon}
        text={freshnessFooterText}
        tooltip={freshnessFooterTooltip}
      />
    </div>
  );
};

// Hoisted function component for metrics breakdown table
function StoreMetricsBreakdownTable({
  selectedStores,
  dateRange,
  deviceType,
}: {
  selectedStores: Store[];
  dateRange: DateRange | undefined;
  deviceType: DeviceType;
}) {
  const { t } = useTranslation();
  const browserTimezone = getBrowserTimezone();
  const currentLanguage = i18n.resolvedLanguage || i18n.language;
  const analytics = AnalyticsServiceV2.getInstance();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  type Row = {
    storeId: number;
    storeName: string;
    // Crave Score
    craveScore?: string; // integer 0..100
    // Key levers
    triedOnValue?: string; // total value tried on (currency)
    triedOnSub?: string; // submetric to mirror analytics card subtitle
    unitsPerSession?: string;
    upsSub?: string; // submetric for units per session (avg session value)
    missedPct?: string;
    missedSub?: string; // submetric for missed requests (total missed value in item requests)
    avgResponseTime?: string;
    avgResponseGoalSub?: string; // submetric for avg response time (goal)
    // Breakdown
    namedShoppers?: string;
    dwellTime?: string;
    customerSatisfaction?: string;
    customerSatisfactionSub?: string; // sublabel: number of shoppers who left reviews
    digitalEngagement?: string;
    // legacy totals kept for potential future use (not displayed in table)
    totalRequests?: string;
    totalRequestsValue?: string;
    frSessions?: string;
    frSessionsValue?: string;
    // Raw values for export
    rawTotalSessions?: number;
    rawTotalItems?: number;
    rawCraveScore?: number;
    rawUnitsPerSession?: number;
    rawMissedPct?: number;
    rawAvgResponseTimeSec?: number;
    rawNamedShoppers?: number;
    rawDwellTimeSec?: number;
    rawCustomerSatisfaction?: number;
    rawTotalRequests?: number;
    rawAvgSessionValue?: number;
    rawTotalSessionValue?: number;
    rawMissedReqCount?: number;
    rawMissedValue?: number;
    rawAvgResponseGoalSec?: number;
    rawShopperRatingsCount?: number;
    rawTotalRequestValue?: number;
    currencyCode?: string;
    currencySymbol?: string;
  };
  const [rows, setRows] = useState<Row[]>([]);
  const [exporting, setExporting] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const localizedTablePagination = useLocalizedTablePagination();
  const loadingKey = useMemo(
    () =>
      [
        selectedStores.map((store) => store.id).join(","),
        dateRange?.start.getTime() ?? "",
        dateRange?.end.getTime() ?? "",
        deviceType,
      ].join("|"),
    [dateRange?.end, dateRange?.start, deviceType, selectedStores],
  );
  const displayedDayCount = useMemo(
    () =>
      dateRange ? getInclusiveStoreDayCount(dateRange.start, dateRange.end, browserTimezone) : 1,
    [browserTimezone, dateRange?.end, dateRange?.start],
  );
  const storeBreakoutLoadingLabels = useMemo(
    () => [
      t("storeComparison.loading.fetchingMetrics"),
      t("storeComparison.loading.calculatingMetrics"),
      t("storeComparison.loading.preparingBreakout"),
    ],
    [t],
  );

  // Dynamic sessions label to match the Sessions card label rules
  const sessionsHeaderLabel = useMemo(() => {
    switch (deviceType) {
      case "RFID_CENTRAL":
        return t("storeComparison.fields.centralSessions");
      case "IN_AISLE":
        return t("storeComparison.fields.inAisleSessions");
      case "FITTING_ROOM":
        return t("storeComparison.fields.fittingRoomSessions");
      case "MOBILE_PICKUP":
        return t("storeComparison.fields.mobileSessions");
      case "all":
      default:
        return t("storeComparison.fields.sessions");
    }
  }, [deviceType, t]);

  // Sorting is supported for the Store column and metrics exposed in the table UI.
  type SortKey =
    | "storeName"
    | "craveScore"
    | "frSessions"
    | "unitsPerSession"
    | "totalRequests"
    | "missedPct"
    | "avgResponseTime"
    | "namedShoppers"
    | "dwellTime"
    | "customerSatisfaction";
  const [sortKey, setSortKey] = useState<SortKey | null>("craveScore");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const getSortValue = (row: Row, key: SortKey): number | string | null => {
    switch (key) {
      case "storeName":
        return row.storeName || null;
      case "craveScore":
        return row.rawCraveScore ?? null;
      case "unitsPerSession":
        return row.rawUnitsPerSession ?? null;
      case "totalRequests":
        return row.rawTotalRequests ?? null;
      case "namedShoppers":
        return row.rawNamedShoppers ?? null;
      case "frSessions": {
        return row.rawTotalSessions ?? null;
      }
      case "avgResponseTime": {
        return row.rawAvgResponseTimeSec ?? null;
      }
      case "missedPct": {
        return row.rawMissedPct ?? null;
      }
      case "dwellTime": {
        return row.rawDwellTimeSec ?? null;
      }
      case "customerSatisfaction": {
        return row.rawCustomerSatisfaction ?? null;
      }
      default:
        return null;
    }
  };

  const requestSort = (key: SortKey) => {
    setPage(0);
    if (sortKey === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "storeName" ? "asc" : "desc");
    }
  };

  const getSortDirectionLabel = (direction: "asc" | "desc") =>
    direction === "asc"
      ? t("storeComparison.sortDirection.ascending")
      : t("storeComparison.sortDirection.descending");

  const getNextSortDirectionLabel = (key: SortKey) =>
    getSortDirectionLabel(
      sortKey === key ? (sortDir === "asc" ? "desc" : "asc") : key === "storeName" ? "asc" : "desc",
    );

  const getSortAriaLabel = (key: SortKey, metric: string) =>
    t("storeComparison.sortByMetric", {
      metric,
      direction: getNextSortDirectionLabel(key),
    });

  type MobileSortKey = SortKey | "";
  const mobileSortOptions = useMemo<FilterMenuOption<MobileSortKey>[]>(
    () => [
      {
        value: "",
        label: t("storeComparison.options.none"),
        showDividerAfter: true,
      },
      {
        value: "storeName",
        label: t("storeComparison.fields.stores"),
      },
      {
        value: "craveScore",
        label: t("storeComparison.fields.craveScore"),
      },
      {
        value: "frSessions",
        label: sessionsHeaderLabel,
      },
      {
        value: "unitsPerSession",
        label: t("storeComparison.fields.unitsPerSession"),
      },
      {
        value: "missedPct",
        label: t("storeComparison.fields.missedRequests"),
      },
      {
        value: "avgResponseTime",
        label: t("storeComparison.fields.averageResponseTime"),
      },
      {
        value: "namedShoppers",
        label: t("storeComparison.fields.namedShoppers"),
      },
      {
        value: "dwellTime",
        label: t("storeComparison.fields.dwellTime"),
      },
      {
        value: "customerSatisfaction",
        label: t("storeComparison.fields.experienceRating"),
      },
    ],
    [sessionsHeaderLabel, t],
  );

  const formatSeconds = (seconds: number): string => {
    if (!isFinite(seconds) || seconds < 0) return "--";
    return formatDurationShort(seconds, i18n.resolvedLanguage || i18n.language);
  };

  const formatMillisToMin = (millis: number): string => {
    if (!isFinite(millis) || millis <= 0) return "--";
    return formatMillisecondsShort(millis, i18n.resolvedLanguage || i18n.language);
  };

  const displayRows = useMemo(() => {
    return rows.map((row) => {
      const triedOnSub = row.rawTotalItems
        ? t("storeComparison.triedOnFormattedSummary", {
            count: formatCompactNumber(row.rawTotalItems, currentLanguage),
            itemsLabel: t("storeComparison.fields.items"),
          })
        : undefined;

      const upsSub =
        row.rawTotalSessions && row.rawAvgSessionValue != null
          ? t("storeComparison.averageSessionValue", {
              value: formatCurrencyWithNoDecimals(
                row.rawAvgSessionValue,
                row.currencyCode as CurrencyCode,
                row.currencySymbol,
              ),
            })
          : undefined;

      const missedSub =
        row.rawTotalRequests && row.rawMissedValue != null
          ? t("storeComparison.itemRequestsSummary", {
              count: formatInteger(row.rawMissedReqCount ?? 0, currentLanguage),
              value: formatCurrencyWithNoDecimals(
                row.rawMissedValue,
                row.currencyCode as CurrencyCode,
                row.currencySymbol,
              ),
            })
          : undefined;

      const avgResponseGoalSub =
        row.rawAvgResponseGoalSec != null
          ? t("storeComparison.goalTime", {
              time: formatSeconds(row.rawAvgResponseGoalSec),
            })
          : undefined;

      return {
        ...row,
        craveScore: row.rawCraveScore != null ? String(row.rawCraveScore) : "--",
        triedOnValue:
          row.rawTotalSessionValue != null
            ? formatCurrencyWithNoDecimals(
                row.rawTotalSessionValue,
                row.currencyCode as CurrencyCode,
                row.currencySymbol,
              )
            : "--",
        triedOnSub,
        unitsPerSession:
          row.rawUnitsPerSession != null
            ? formatDecimal(row.rawUnitsPerSession, currentLanguage, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : "--",
        upsSub,
        missedPct:
          row.rawMissedPct != null
            ? formatPercentFromWhole(row.rawMissedPct, currentLanguage)
            : "--",
        missedSub,
        avgResponseTime:
          row.rawAvgResponseTimeSec != null ? formatSeconds(row.rawAvgResponseTimeSec) : "--",
        avgResponseGoalSub,
        namedShoppers: row.rawNamedShoppers
          ? formatInteger(row.rawNamedShoppers, currentLanguage)
          : "--",
        dwellTime: row.rawDwellTimeSec != null ? formatSeconds(row.rawDwellTimeSec) : "--",
        customerSatisfaction:
          row.rawCustomerSatisfaction != null
            ? formatDecimal(row.rawCustomerSatisfaction, currentLanguage, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })
            : "--",
        customerSatisfactionSub: row.rawShopperRatingsCount
          ? t("storeComparison.reviewsFromShoppers", {
              count: row.rawShopperRatingsCount,
            })
          : undefined,
        totalRequests: row.rawTotalRequests
          ? formatInteger(row.rawTotalRequests, currentLanguage)
          : "--",
        totalRequestsValue:
          row.rawTotalRequestValue != null
            ? formatCurrencyWithNoDecimals(
                row.rawTotalRequestValue,
                row.currencyCode as CurrencyCode,
                row.currencySymbol,
              )
            : "--",
        frSessions: row.rawTotalSessions
          ? formatCompactNumber(row.rawTotalSessions, currentLanguage)
          : "--",
        frSessionsValue:
          row.rawTotalSessionValue != null
            ? formatCurrencyWithNoDecimals(
                row.rawTotalSessionValue,
                row.currencyCode as CurrencyCode,
                row.currencySymbol,
              )
            : "--",
      };
    });
  }, [rows, currentLanguage, t]);

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    const copy = [...displayRows];
    copy.sort((a, b) => {
      const av = getSortValue(a, sortKey);
      const bv = getSortValue(b, sortKey);
      const aNull = av === null;
      const bNull = bv === null;
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      const cmp =
        typeof av === "string" && typeof bv === "string"
          ? av.localeCompare(bv)
          : Number(av) - Number(bv);
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [displayRows, sortKey, sortDir]);
  const maxPage = Math.max(Math.ceil(sortedRows.length / rowsPerPage) - 1, 0);
  const currentPage = Math.min(page, maxPage);
  const paginatedRows = sortedRows.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage,
  );
  const renderPagination = (compact = false) => (
    <TablePagination
      component="div"
      count={sortedRows.length}
      page={currentPage}
      rowsPerPage={rowsPerPage}
      rowsPerPageOptions={[25, 50, 100]}
      onPageChange={(_, nextPage) => setPage(nextPage)}
      onRowsPerPageChange={(event) => {
        setRowsPerPage(Number(event.target.value));
        setPage(0);
      }}
      sx={compact ? { "& .MuiToolbar-root": { minHeight: 0, px: 0 } } : undefined}
      {...localizedTablePagination}
    />
  );

  const handleExport = () => {
    if (displayRows.length === 0) return;
    setExporting(true);

    try {
      const headers = [
        t("storeComparison.fields.stores"),
        t("storeComparison.fields.craveScore"),
        sessionsHeaderLabel,
        t("storeComparison.fields.unitsPerSession"),
        t("storeComparison.fields.averageSessionValue"),
        t("storeComparison.fields.totalRequests"),
        t("storeComparison.fields.totalRequestsValue"),
        t("storeComparison.fields.missedRequestsPercent"),
        t("storeComparison.fields.missedRequestsCount"),
        t("storeComparison.fields.missedRequestsValue"),
        t("storeComparison.fields.averageResponseTime"),
        t("storeComparison.fields.averageResponseTimeGoalSeconds"),
        t("storeComparison.fields.namedShoppers"),
        t("storeComparison.fields.dwellTime"),
        t("storeComparison.fields.experienceRating"),
        t("storeComparison.fields.experienceRatingReviews"),
      ];

      const csvRows = [
        headers.join(","),
        ...displayRows.map((row) => {
          const values = [
            row.storeName,
            row.craveScore ?? "--",
            row.frSessions ?? "--",
            row.unitsPerSession ?? "--",
            row.rawAvgSessionValue != null
              ? formatCurrencyCSV(
                  row.rawAvgSessionValue,
                  row.currencyCode as CurrencyCode,
                  row.currencySymbol,
                )
              : "--",
            row.totalRequests ?? "--",
            row.rawTotalRequestValue != null
              ? formatCurrencyCSV(
                  row.rawTotalRequestValue,
                  row.currencyCode as CurrencyCode,
                  row.currencySymbol,
                )
              : "--",
            row.missedPct ?? "--",
            row.rawMissedReqCount ?? "--",
            row.rawMissedValue != null
              ? formatCurrencyCSV(
                  row.rawMissedValue,
                  row.currencyCode as CurrencyCode,
                  row.currencySymbol,
                )
              : "--",
            row.avgResponseTime ?? "--",
            row.rawAvgResponseGoalSec ?? "--",
            row.namedShoppers ?? "--",
            row.dwellTime ?? "--",
            row.customerSatisfaction ?? "--",
            row.rawShopperRatingsCount ?? "--",
          ];

          return values
            .map((value) => {
              const str = String(value ?? "");
              if (str.includes(",") || str.includes('"') || str.includes("\n")) {
                return `"${str.replace(/"/g, '""')}"`;
              }
              return str;
            })
            .join(",");
        }),
      ];

      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `multi_store_overview_${formatStoreDateTimeFileStamp(new Date(), getBrowserTimezone())}.csv`,
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error("Export failed", e);
    } finally {
      setExporting(false);
    }
  };

  // Render numeric rating with stars out of 5
  const renderStarRating = (val: string | undefined): React.ReactNode => {
    if (!val || val === "--") return "--";
    const rating = parseFloat(val);
    if (isNaN(rating)) return val;
    const full = Math.min(5, Math.max(0, Math.round(rating)));
    const fullStars = "★".repeat(full);
    const emptyStars = "☆".repeat(5 - full);
    return (
      <span
        className="inline-flex items-center gap-1"
        aria-label={t("storeComparison.ratingOutOfFive", { rating })}
        title={t("storeComparison.ratingTooltip", { rating })}
      >
        <span>{val}</span>
        <span className="text-yellow-500">
          {fullStars}
          {emptyStars}
        </span>
      </span>
    );
  };

  useEffect(() => {
    const ids = selectedStores.map((s) => s.id);
    if (!ids.length || !dateRange) {
      setRows([]);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);

    const startDate = storePerformanceService.formatDate(dateRange.start, browserTimezone);
    const endDate = storePerformanceService.formatDate(dateRange.end, browserTimezone);

    let cancelled = false;
    setRows([]);

    console.log(`getByStoreIdsAndDateRange from ${startDate} to ${endDate}`);

    (async () => {
      try {
        // Batch fetch core analytics by store
        const [
          sessionMapResp,
          requestTotalMapResp,
          requestMissedMapResp,
          requestCompletedMapResp,
          requestCantFindMapResp,
          feedbackMapResp,
        ] = await Promise.all([
          analytics.getSessionsStatsByStore({
            storeIds: ids,
            startDate,
            endDate,
            deviceType,
          }),
          // All requests (totals)
          analytics.getRequestStatsByStore({
            storeIds: ids,
            startDate,
            endDate,
            deviceType,
          }),
          // Missed requests only
          analytics.getRequestStatsByStore({
            storeIds: ids,
            startDate,
            endDate,
            requestStatus: "MISSED",
            deviceType,
          }),
          // Completed requests only (to align Avg Response Time with cards)
          analytics.getRequestStatsByStore({
            storeIds: ids,
            startDate,
            endDate,
            requestStatus: "COMPLETED",
            deviceType,
          }),
          // Can't finds only (for Crave Score parity)
          analytics.getRequestStatsByStore({
            storeIds: ids,
            startDate,
            endDate,
            requestType: "CANT_FIND",
            deviceType,
          }),
          // Shopper feedback averages
          analytics.getShopperFeedbackAverageByStore({
            storeIds: ids,
            startDate,
            endDate,
          }),
        ]);

        // Fetch store goals (no by-store endpoint available)
        const customerService = CustomerService.getInstance();
        const goalMap: Record<number, number | undefined> = {};
        await Promise.all(
          ids.map(async (storeId) => {
            try {
              const resp = await customerService.getStoreGoal(storeId);
              const seconds = (resp as any)?.data?.timeGoal;
              if (typeof seconds === "number") goalMap[storeId] = seconds;
            } catch {}
          }),
        );

        const sessionMap = (sessionMapResp?.data || {}) as Record<string, any>;
        const reqTotalMap = (requestTotalMapResp?.data || {}) as Record<string, any>;
        const reqMissedMap = (requestMissedMapResp?.data || {}) as Record<string, any>;
        const reqCompletedMap = (requestCompletedMapResp?.data || {}) as Record<string, any>;
        const reqCantFindMap = (requestCantFindMapResp?.data || {}) as Record<string, any>;
        const feedbackMap = (feedbackMapResp?.data || {}) as Record<string, any>;

        const built: Row[] = selectedStores.map((s) => {
          const key = String(s.id);
          const session = sessionMap[key] || {};
          const reqTotal = reqTotalMap[key] || {};
          const reqMissed = reqMissedMap[key] || {};
          const feedback = feedbackMap[key] || {};

          const totalSessions = session.totalSessions ?? 0;
          const totalSessionsIncludingNoItems = session.totalSessionsIncludingNoItems ?? 0;
          const sessionCount =
            deviceType === "IN_AISLE" ? totalSessionsIncludingNoItems : totalSessions;
          const totalItems = session.totalItems ?? 0;
          const totalDwellMs = session.totalDwellMillisecond ?? 0;
          const totalDwellMsIncludingNoItems = session.totalDwellMillisecondIncludingNoItems ?? 0;
          const dwellDenominator = deviceType === "IN_AISLE" ? sessionCount : totalSessions;
          const dwellTotalMs =
            deviceType === "IN_AISLE" ? totalDwellMsIncludingNoItems : totalDwellMs;
          const named = session.numberOfNamedShoppers ?? 0;
          const engaged = session.numberOfEngagedShoppers ?? 0;
          const totalSessionValue = session.totalSessionValue ?? null;

          const totalReq = reqTotal.totalRequests ?? 0;
          const totalRequestValue = reqTotal.totalRequestValue ?? null;
          const missedReq = reqMissed.totalRequests ?? 0;
          const missedValue = (reqMissed.totalRequestValue ?? null) as number | null;

          const shopperRatings = feedback.numberOfShopperRating ?? 0;
          const totalShopperRating = feedback.totalShopperRating ?? 0;

          const unitsPerSessionDenominator =
            deviceType === "IN_AISLE" ? totalSessionsIncludingNoItems : totalSessions;
          const unitsPerSessionValue =
            unitsPerSessionDenominator > 0 ? totalItems / unitsPerSessionDenominator : null;
          const unitsPerSession =
            unitsPerSessionValue != null
              ? formatDecimal(unitsPerSessionValue, i18n.resolvedLanguage || i18n.language, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : "--";
          // Use COMPLETED requests for Avg Response Time (parity with cards); fallback to totals if none
          const reqCompleted = reqCompletedMap[key] || {};
          const completedReq = reqCompleted.totalRequests ?? 0;
          const completedTimeSec = reqCompleted.totalTimeTakenSeconds ?? 0;
          // Compute Avg Response Time using COMPLETED requests only. If there are no completed
          // requests but there are requests (all missed/cancelled), treat average as 0s to match
          // the top card UX where no completed requests implies 0s rather than inflating with ALL.
          const avgRespSecondsForDisplay =
            completedReq > 0 ? completedTimeSec / completedReq : totalReq > 0 ? 0 : null;
          const avgRespTime =
            avgRespSecondsForDisplay != null ? formatSeconds(avgRespSecondsForDisplay) : "--";
          const missedPctValue = totalReq > 0 ? (missedReq / totalReq) * 100 : null;
          const pct = (value: number | null) =>
            value != null
              ? formatPercentFromWhole(value, i18n.resolvedLanguage || i18n.language)
              : "--";
          const dwellStr =
            dwellDenominator > 0 ? formatMillisToMin(dwellTotalMs / dwellDenominator) : "--";
          const csatValue =
            shopperRatings > 0 && totalShopperRating > 0
              ? totalShopperRating / shopperRatings
              : null;
          const csat =
            csatValue != null
              ? formatDecimal(csatValue, i18n.resolvedLanguage || i18n.language, {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                })
              : "--";
          const adjustedEngaged = Math.round(engaged * 0.8);
          const engagementPct =
            totalSessions > 0
              ? formatPercentFromWhole(
                  (adjustedEngaged / totalSessions) * 100,
                  i18n.resolvedLanguage || i18n.language,
                )
              : "--";

          const [currencyCode, currencySymbol] = getStoreCurrencyIdentifiers([s]);

          const triedOnSub =
            totalItems > 0
              ? t("storeComparison.triedOnFormattedSummary", {
                  count: formatCompactNumber(totalItems, i18n.resolvedLanguage || i18n.language),
                  itemsLabel: t("storeComparison.fields.items"),
                })
              : undefined;
          const upsSub =
            sessionCount > 0 && totalSessionValue != null
              ? t("storeComparison.averageSessionValue", {
                  value: formatCurrencyWithNoDecimals(
                    Math.round(totalSessionValue / sessionCount),
                    currencyCode as CurrencyCode,
                    currencySymbol,
                  ),
                })
              : undefined;
          const missedSub =
            totalReq > 0 && missedValue != null
              ? t("storeComparison.itemRequestsSummary", {
                  count: formatInteger(missedReq, i18n.resolvedLanguage || i18n.language),
                  value: formatCurrencyWithNoDecimals(
                    missedValue,
                    currencyCode as CurrencyCode,
                    currencySymbol,
                  ),
                })
              : undefined;
          const timeGoalSeconds = goalMap[s.id];
          const avgResponseGoalSub =
            timeGoalSeconds != null
              ? t("storeComparison.goalTime", {
                  time: formatSeconds(Number(timeGoalSeconds)),
                })
              : undefined;

          // Compute Crave Score for this store
          const reqCantFind = reqCantFindMap[key] || {};
          const cantFindReq = reqCantFind.totalRequests ?? 0;
          const cantFindPctValue = totalReq > 0 ? (cantFindReq / totalReq) * 100 : null;
          // For Crave: use COMPLETED-only avg; if no COMPLETED requests, pass null so it doesn't award points
          const avgResponseSeconds = completedReq > 0 ? completedTimeSec / completedReq : null;
          const craveInputsRow = [
            {
              id: "sessions",
              label: sessionsHeaderLabel,
              rawValue: sessionCount,
            },
            {
              id: "ups",
              label: t("storeComparison.fields.unitsPerSession"),
              rawValue: unitsPerSessionValue,
            },
            {
              id: "missedPct",
              label: t("storeComparison.fields.missedRequestsPercent"),
              rawValue: missedPctValue,
            },
            {
              id: "avgResponseSec",
              label: t("storeComparison.fields.averageResponseTimeSeconds"),
              rawValue: avgResponseSeconds,
            },
            // Optional/if available: these will be ignored if missing in config
            {
              id: "satisfaction",
              label: t("storeComparison.fields.experienceRating"),
              rawValue:
                shopperRatings > 0 && totalShopperRating > 0
                  ? totalShopperRating / shopperRatings
                  : null,
            },
            {
              id: "totalRequests",
              label: t("storeComparison.fields.totalRequests"),
              rawValue: totalReq,
            },
            {
              id: "cantFindPct",
              label: t("storeComparison.fields.cantFindsPercent"),
              rawValue: cantFindPctValue,
            },
            {
              id: "fulfilledPct",
              label: t("storeComparison.fields.fulfilledRequestsPercent"),
              rawValue: null,
            },
            {
              id: "dwellSec",
              label: t("storeComparison.fields.dwellTimeSeconds"),
              rawValue:
                dwellDenominator > 0 ? Math.round(dwellTotalMs / 1000 / dwellDenominator) : null,
            },
            {
              id: "namedShoppersPct",
              label: t("storeComparison.fields.namedShoppersPercent"),
              rawValue: null,
            },
          ];
          const craveScoreVal = (() => {
            try {
              const res = calculateCraveScore(
                getCraveScoreConfigForDevice(deviceType),
                craveInputsRow,
              );
              if (!res || !isFinite(res.score)) return null;
              return Math.round(res.score);
            } catch {
              return null;
            }
          })();

          return {
            storeId: s.id,
            storeName: s.name,
            craveScore: craveScoreVal != null ? String(craveScoreVal) : "--",
            triedOnValue:
              totalSessionValue != null
                ? formatCurrencyWithNoDecimals(
                    totalSessionValue,
                    currencyCode as CurrencyCode,
                    currencySymbol,
                  )
                : "--",
            triedOnSub,
            unitsPerSession,
            upsSub,
            missedPct: pct(missedPctValue),
            missedSub,
            avgResponseTime: avgRespTime,
            avgResponseGoalSub,
            namedShoppers: named
              ? formatInteger(named, i18n.resolvedLanguage || i18n.language)
              : "--",
            dwellTime: dwellStr,
            customerSatisfaction: csat,
            customerSatisfactionSub:
              shopperRatings > 0
                ? t("storeComparison.reviewsFromShoppers", {
                    count: shopperRatings,
                  })
                : undefined,
            digitalEngagement: engagementPct,
            totalRequests: totalReq
              ? formatInteger(totalReq, i18n.resolvedLanguage || i18n.language)
              : "--",
            totalRequestsValue:
              totalRequestValue != null
                ? formatCurrencyWithNoDecimals(
                    totalRequestValue,
                    currencyCode as CurrencyCode,
                    currencySymbol,
                  )
                : "--",
            frSessions: sessionCount
              ? formatCompactNumber(sessionCount, i18n.resolvedLanguage || i18n.language)
              : "--",
            frSessionsValue:
              totalSessionValue != null
                ? formatCurrencyWithNoDecimals(
                    totalSessionValue,
                    currencyCode as CurrencyCode,
                    currencySymbol,
                  )
                : "--",
            // Raw values
            rawTotalSessions: sessionCount,
            rawTotalItems: totalItems,
            rawCraveScore: craveScoreVal ?? undefined,
            rawUnitsPerSession: unitsPerSessionValue ?? undefined,
            rawMissedPct: missedPctValue ?? undefined,
            rawAvgResponseTimeSec: avgRespSecondsForDisplay ?? undefined,
            rawNamedShoppers: named,
            rawDwellTimeSec:
              dwellDenominator > 0 ? Math.round(dwellTotalMs / 1000 / dwellDenominator) : undefined,
            rawCustomerSatisfaction: csatValue ?? undefined,
            rawTotalRequests: totalReq,
            rawAvgSessionValue:
              sessionCount > 0 && totalSessionValue != null
                ? Math.round(totalSessionValue / sessionCount)
                : undefined,
            rawTotalSessionValue: totalSessionValue ?? undefined,
            rawMissedReqCount: missedReq,
            rawMissedValue: missedValue ?? undefined,
            rawAvgResponseGoalSec: timeGoalSeconds != null ? Number(timeGoalSeconds) : undefined,
            rawShopperRatingsCount: shopperRatings,
            rawTotalRequestValue: totalRequestValue ?? undefined,
            currencyCode,
            currencySymbol,
          };
        });

        if (!cancelled) {
          setRows(built);
          setLoading(false);
        }
      } catch (e: any) {
        if (!cancelled) {
          console.error("Failed to load breakdown (batched)", e);
          setError(e?.message || t("storeComparison.errors.failedLoadBreakdown"));
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    selectedStores.map((s) => s.id).join(","),
    selectedStores.map((s) => s.currencySymbol ?? "").join("|"),
    storePerformanceService.formatDate(dateRange?.start, browserTimezone),
    storePerformanceService.formatDate(dateRange?.end, browserTimezone),
    deviceType,
  ]);

  const exportAction = (
    <Tooltip title={t("storeComparison.actions.exportCsv")}>
      <span>
        <IconButton
          size="small"
          onClick={handleExport}
          disabled={loading || rows.length === 0 || exporting}
          sx={{ color: "rgb(59 130 246)" }}
        >
          <IosShare fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );

  if (!selectedStores.length) return null;

  return (
    <div className="mt-8">
      {error && <div className="text-red-500 text-center py-2">{error}</div>}
      <>
        {/* Large-screen view: keep table for scanability */}
        <ComparisonBreakdownPanel
          sx={{
            display: { xs: "none", md: "block" },
            overflow: "visible",
          }}
          title={t("storeComparison.sections.storeBreakout")}
          actions={renderPagination(true)}
          exportAction={exportAction}
          footer={renderPagination()}
        >
          {loading ? (
            <Box sx={{ mb: 2.5 }}>
              <LongRunningDataLoader
                variant="table"
                isLoading={true}
                baseLabels={storeBreakoutLoadingLabels}
                loopingLabels={[
                  t("storeInsights.common.loading.stillWorking"),
                  t("storeInsights.common.loading.thanksForPatience"),
                ]}
                estimate={{
                  meanTimePerStoreMs: 300,
                  storeCount: selectedStores.length,
                  displayedDayCount,
                  windowDayGrowthRate: 0.002,
                  growth: "logarithmic",
                }}
                locale={currentLanguage}
                loadingKey={loadingKey}
                estimateLabel={(duration) =>
                  t("storeComparison.loading.usuallyUnder", { duration })
                }
              />
            </Box>
          ) : null}
          <Box sx={{ overflowX: "auto" }}>
            <Table
              sx={{
                ...tableInCardSx,
                minWidth: 1200,
                "& .MuiTableHead-root th": {
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark" ? "#374151" : "#F3F4F6",
                  borderTop: "1px solid",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  fontSize: (theme) => theme.typography.body2.fontSize,
                  padding: (theme) => theme.spacing(1, 1.5),
                },
                "& .breakout-store-header, & .breakout-store-cell": {
                  borderInlineEnd: "1px solid",
                  borderColor: "divider",
                },
                "& .breakout-store-header": {
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark" ? "#374151" : "#F3F4F6",
                  insetInlineStart: 0,
                  position: "sticky",
                  zIndex: 2,
                },
                "& .breakout-store-cell": {
                  insetInlineStart: 0,
                  position: "sticky",
                  zIndex: 1,
                },
                "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(odd) .breakout-store-cell": {
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark" ? "#111827" : "#FFFFFF",
                },
                "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even) .breakout-store-cell": {
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark" ? "#1F2937" : "#F9FAFB",
                },
                "& .breakout-crave-score-header, & .breakout-crave-score-cell": {
                  textAlign: "center",
                  borderInlineEnd: (theme) => `1px solid ${theme.palette.divider}`,
                },
                "& .breakout-section-header": {
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  fontSize: "0.875rem",
                  letterSpacing: "0.08em",
                  textAlign: "center",
                  textTransform: "uppercase",
                },
                "& .breakout-breakdown-header": {
                  borderInlineStart: (theme) => `1px solid ${theme.palette.divider} !important`,
                },
                "& .breakout-named-shoppers-header, & .breakout-named-shoppers-cell": {
                  borderInlineStart: "1px solid",
                  borderColor: "divider",
                },
                "& .MuiTableHead-root .MuiTableRow-root:nth-of-type(2) .MuiTableCell-root": {
                  borderTop: 0,
                },
                "& .MuiTableBody-root .MuiTableCell-root": {
                  borderBottom: 0,
                  fontSize: (theme) => theme.typography.body2.fontSize,
                  padding: (theme) => theme.spacing(1, 1.5),
                },
                "& .MuiTableSortLabel-root": {
                  alignItems: "center",
                  display: "inline-flex",
                  gap: 0.5,
                  maxWidth: "100%",
                },
                "& .MuiTableSortLabel-root > span": {
                  minWidth: 0,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  {/* Group headers */}
                  <TableCell
                    component="th"
                    className="breakout-store-header px-3 py-2 text-gray-700 bg-transparent text-left rtl:text-right sticky left-0 rtl:left-auto rtl:right-0"
                    style={{ fontWeight: 600, maxWidth: 220, minWidth: 220, width: 220 }}
                    rowSpan={2}
                    aria-sort={
                      sortKey === "storeName"
                        ? sortDir === "asc"
                          ? "ascending"
                          : "descending"
                        : "none"
                    }
                  >
                    <TableSortLabel
                      active={sortKey === "storeName"}
                      direction={sortKey === "storeName" ? sortDir : "asc"}
                      onClick={() => requestSort("storeName")}
                      aria-label={getSortAriaLabel("storeName", t("storeComparison.fields.stores"))}
                    >
                      <span className="text-gray-700" style={{ fontWeight: 500 }}>
                        {t("storeComparison.fields.stores")}
                      </span>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    component="th"
                    className="breakout-crave-score-header px-3 py-2 text-gray-700 text-left rtl:text-right"
                    style={{ fontWeight: 600 }}
                    rowSpan={2}
                    aria-sort={
                      sortKey === "craveScore"
                        ? sortDir === "asc"
                          ? "ascending"
                          : "descending"
                        : "none"
                    }
                  >
                    <TableSortLabel
                      active={sortKey === "craveScore"}
                      direction={sortKey === "craveScore" ? sortDir : "asc"}
                      onClick={() => requestSort("craveScore")}
                      aria-label={getSortAriaLabel(
                        "craveScore",
                        t("storeComparison.fields.craveScore"),
                      )}
                    >
                      <span className="text-gray-700" style={{ fontWeight: 500 }}>
                        {t("storeComparison.fields.craveScore")}
                      </span>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    component="th"
                    className="breakout-key-levers-header breakout-section-header px-3 py-2 text-center text-gray-700"
                    style={{ fontWeight: 600 }}
                    colSpan={5}
                    scope="colgroup"
                  >
                    {t("storeComparison.sections.keyLevers")}
                  </TableCell>
                  <TableCell
                    component="th"
                    className="breakout-breakdown-header breakout-section-header px-3 py-2 text-center text-gray-700"
                    style={{ fontWeight: 600 }}
                    colSpan={3}
                    scope="colgroup"
                  >
                    {t("storeComparison.sections.breakdown")}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    component="th"
                    className="breakout-sessions-header px-1 py-0 bg-transparent text-left rtl:text-right"
                    aria-sort={
                      sortKey === "frSessions"
                        ? sortDir === "asc"
                          ? "ascending"
                          : "descending"
                        : "none"
                    }
                  >
                    <TableSortLabel
                      active={sortKey === "frSessions"}
                      direction={sortKey === "frSessions" ? sortDir : "asc"}
                      onClick={() => requestSort("frSessions")}
                      aria-label={getSortAriaLabel("frSessions", sessionsHeaderLabel)}
                    >
                      <span className="text-gray-700" style={{ fontWeight: 500 }}>
                        {sessionsHeaderLabel}
                      </span>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    component="th"
                    className="px-1 py-0 bg-transparent text-left rtl:text-right"
                    aria-sort={
                      sortKey === "unitsPerSession"
                        ? sortDir === "asc"
                          ? "ascending"
                          : "descending"
                        : "none"
                    }
                  >
                    <TableSortLabel
                      active={sortKey === "unitsPerSession"}
                      direction={sortKey === "unitsPerSession" ? sortDir : "asc"}
                      onClick={() => requestSort("unitsPerSession")}
                      aria-label={getSortAriaLabel(
                        "unitsPerSession",
                        t("storeComparison.fields.unitsPerSession"),
                      )}
                    >
                      <span className="text-gray-700" style={{ fontWeight: 500 }}>
                        {t("storeComparison.fields.unitsPerSession")}
                      </span>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    component="th"
                    className="px-1 py-0 bg-transparent text-left rtl:text-right"
                    aria-sort={
                      sortKey === "totalRequests"
                        ? sortDir === "asc"
                          ? "ascending"
                          : "descending"
                        : "none"
                    }
                  >
                    <TableSortLabel
                      active={sortKey === "totalRequests"}
                      direction={sortKey === "totalRequests" ? sortDir : "asc"}
                      onClick={() => requestSort("totalRequests")}
                      aria-label={getSortAriaLabel(
                        "totalRequests",
                        t("storeComparison.fields.totalRequests"),
                      )}
                    >
                      <span className="text-gray-700" style={{ fontWeight: 500 }}>
                        {t("storeComparison.fields.totalRequests")}
                      </span>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    component="th"
                    className="px-1 py-0 bg-transparent text-left rtl:text-right"
                    aria-sort={
                      sortKey === "missedPct"
                        ? sortDir === "asc"
                          ? "ascending"
                          : "descending"
                        : "none"
                    }
                  >
                    <TableSortLabel
                      active={sortKey === "missedPct"}
                      direction={sortKey === "missedPct" ? sortDir : "asc"}
                      onClick={() => requestSort("missedPct")}
                      aria-label={getSortAriaLabel(
                        "missedPct",
                        t("storeComparison.fields.missedRequests"),
                      )}
                    >
                      <span className="text-gray-700" style={{ fontWeight: 500 }}>
                        {t("storeComparison.fields.missedRequests")}
                      </span>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    component="th"
                    className="px-1 py-0 bg-transparent text-left rtl:text-right"
                    aria-sort={
                      sortKey === "avgResponseTime"
                        ? sortDir === "asc"
                          ? "ascending"
                          : "descending"
                        : "none"
                    }
                  >
                    <TableSortLabel
                      active={sortKey === "avgResponseTime"}
                      direction={sortKey === "avgResponseTime" ? sortDir : "asc"}
                      onClick={() => requestSort("avgResponseTime")}
                      aria-label={getSortAriaLabel(
                        "avgResponseTime",
                        t("storeComparison.fields.averageResponseTime"),
                      )}
                    >
                      <span className="text-gray-700" style={{ fontWeight: 500 }}>
                        {t("storeComparison.fields.averageResponseTime")}
                      </span>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    component="th"
                    className="breakout-named-shoppers-header px-1 py-0 text-left rtl:text-right"
                    aria-sort={
                      sortKey === "namedShoppers"
                        ? sortDir === "asc"
                          ? "ascending"
                          : "descending"
                        : "none"
                    }
                  >
                    <TableSortLabel
                      active={sortKey === "namedShoppers"}
                      direction={sortKey === "namedShoppers" ? sortDir : "asc"}
                      onClick={() => requestSort("namedShoppers")}
                      aria-label={getSortAriaLabel(
                        "namedShoppers",
                        t("storeComparison.fields.namedShoppers"),
                      )}
                    >
                      <span className="text-gray-700" style={{ fontWeight: 500 }}>
                        {t("storeComparison.fields.namedShoppers")}
                      </span>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    component="th"
                    className="px-1 py-0 text-left rtl:text-right"
                    aria-sort={
                      sortKey === "dwellTime"
                        ? sortDir === "asc"
                          ? "ascending"
                          : "descending"
                        : "none"
                    }
                  >
                    <TableSortLabel
                      active={sortKey === "dwellTime"}
                      direction={sortKey === "dwellTime" ? sortDir : "asc"}
                      onClick={() => requestSort("dwellTime")}
                      aria-label={getSortAriaLabel(
                        "dwellTime",
                        t("storeComparison.fields.dwellTime"),
                      )}
                    >
                      <span className="text-gray-700" style={{ fontWeight: 500 }}>
                        {t("storeComparison.fields.dwellTime")}
                      </span>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    component="th"
                    className="px-1 py-0 text-left rtl:text-right"
                    aria-sort={
                      sortKey === "customerSatisfaction"
                        ? sortDir === "asc"
                          ? "ascending"
                          : "descending"
                        : "none"
                    }
                  >
                    <TableSortLabel
                      active={sortKey === "customerSatisfaction"}
                      direction={sortKey === "customerSatisfaction" ? sortDir : "asc"}
                      onClick={() => requestSort("customerSatisfaction")}
                      aria-label={getSortAriaLabel(
                        "customerSatisfaction",
                        t("storeComparison.fields.experienceRating"),
                      )}
                    >
                      <span className="text-gray-700" style={{ fontWeight: 500 }}>
                        {t("storeComparison.fields.experienceRating")}
                      </span>
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableSkeletonRows columnCount={10} />
                ) : (
                  paginatedRows.map((r) => (
                    <TableRow key={r.storeId}>
                      <TableCell
                        className="breakout-store-cell px-3 py-2 text-gray-900 bg-inherit sticky left-0 rtl:left-auto rtl:right-0"
                        style={{ fontWeight: 700, maxWidth: 220, minWidth: 220, width: 220 }}
                      >
                        <Box
                          sx={{
                            maxWidth: 196,
                            overflowWrap: "anywhere",
                            whiteSpace: "normal",
                          }}
                        >
                          {r.storeName}
                        </Box>
                      </TableCell>
                      {/* Crave Score column */}
                      <TableCell className="breakout-crave-score-cell px-3 py-2">
                        <div className="inline-flex items-center gap-2">
                          <span
                            className={
                              r.rawCraveScore == null || r.rawCraveScore === 0
                                ? "font-semibold text-gray-900"
                                : r.rawCraveScore > 0 && r.rawCraveScore < 75
                                  ? "inline-block bg-gradient-to-r from-[#FF9F0A] to-[#FF3B30] bg-clip-text font-semibold text-transparent"
                                  : "inline-block bg-gradient-to-r from-[#0066ff] to-[#00B7FF] bg-clip-text font-semibold text-transparent"
                            }
                          >
                            {r.craveScore ?? "--"}
                          </span>
                        </div>
                      </TableCell>
                      {/* Key Levers group */}
                      <TableCell className="breakout-sessions-cell px-3 py-2">
                        <div className="flex flex-col leading-tight">
                          <div className="text-gray-900" style={{ fontWeight: 500 }}>
                            {r.frSessions ?? "--"}
                          </div>
                          {r.triedOnSub && (
                            <div className="text-gray-500 mt-0.5" style={{ fontSize: "11px" }}>
                              {r.triedOnSub}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="px-3 py-2">
                        <div className="flex flex-col leading-tight">
                          <div className="text-gray-900" style={{ fontWeight: 500 }}>
                            {r.unitsPerSession ?? "--"}
                          </div>
                          {r.upsSub && (
                            <div className="text-gray-500 mt-0.5" style={{ fontSize: "11px" }}>
                              {r.upsSub}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="px-3 py-2">
                        <div className="flex flex-col leading-tight">
                          <div className="text-gray-900" style={{ fontWeight: 500 }}>
                            {r.totalRequests ?? "--"}
                          </div>
                          {r.totalRequestsValue && r.totalRequestsValue !== "--" && (
                            <div className="text-gray-500 mt-0.5" style={{ fontSize: "11px" }}>
                              {t("storeComparison.itemRequestsSummary", {
                                count: r.totalRequests,
                                value: r.totalRequestsValue,
                              })}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="px-3 py-2">
                        <div className="flex flex-col leading-tight">
                          <div className="text-gray-900" style={{ fontWeight: 500 }}>
                            {r.missedPct ?? "--"}
                          </div>
                          {r.missedSub && (
                            <div className="text-gray-500 mt-0.5" style={{ fontSize: "11px" }}>
                              {r.missedSub}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="px-3 py-2">
                        <div className="flex flex-col leading-tight">
                          <div className="text-gray-900" style={{ fontWeight: 500 }}>
                            {r.avgResponseTime ?? "--"}
                          </div>
                          {r.avgResponseGoalSub && (
                            <div className="text-gray-500 mt-0.5" style={{ fontSize: "11px" }}>
                              {r.avgResponseGoalSub}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      {/* Breakdown group */}
                      <TableCell className="breakout-named-shoppers-cell px-3 py-2">
                        {r.namedShoppers ?? "--"}
                      </TableCell>
                      <TableCell className="px-3 py-2">{r.dwellTime ?? "--"}</TableCell>
                      <TableCell className="px-3 py-2">
                        <div className="flex flex-col leading-tight">
                          <div className="text-gray-900" style={{ fontWeight: 500 }}>
                            {renderStarRating(r.customerSatisfaction)}
                          </div>
                          {r.customerSatisfactionSub && (
                            <div className="text-gray-500 mt-0.5" style={{ fontSize: "11px" }}>
                              {r.customerSatisfactionSub}
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
                {!loading && sortedRows.length === 0 && (
                  <TableRow className="table-empty-state">
                    <TableCell className="px-3 py-6 text-center text-gray-500" colSpan={10}>
                      {t("storeComparison.empty.noDataForSelection")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </ComparisonBreakdownPanel>

        {/* Mobile list within the section Paper */}
        <ComparisonBreakdownPanel
          sx={{
            display: { xs: "block", md: "none" },
          }}
          title={t("storeComparison.sections.storeBreakout")}
          actions={renderPagination(true)}
          exportAction={exportAction}
          footer={renderPagination()}
        >
          {/* Mobile controls: sort by selector and direction toggle */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
            <SortSelectorControl
              value={sortKey ?? ""}
              options={mobileSortOptions}
              onChange={(value) => {
                setPage(0);
                if (!value) {
                  setSortKey(null);
                  return;
                }

                setSortKey(value);
                setSortDir(value === "storeName" ? "asc" : "desc");
              }}
              direction={sortDir}
              onDirectionChange={(direction) => {
                setPage(0);
                setSortDir(direction);
              }}
              label={t("storeComparison.actions.sortBy")}
              menuTitle={t("storeComparison.actions.sortBy")}
              directionAriaLabel={t("storeComparison.aria.toggleSortDirection", {
                direction: getSortDirectionLabel(sortDir === "asc" ? "desc" : "asc"),
              })}
              directionTitle={
                !sortKey
                  ? t("storeComparison.hints.selectMetricToSort")
                  : t("storeComparison.hints.direction", {
                      direction: getSortDirectionLabel(sortDir),
                    })
              }
              directionDisabled={!sortKey}
            />
          </Box>

          <RecordList
            items={paginatedRows}
            getItemId={(r) => r.storeId}
            loading={loading}
            resetKey={`${currentPage}:${rowsPerPage}:${sortKey}:${sortDir}:${paginatedRows.map((r) => r.storeId).join(",")}`}
            renderTitle={(r) => r.storeName}
            renderSummary={(r) => (
              <RecordFields
                fields={[
                  { label: t("storeComparison.fields.craveScore"), value: r.craveScore ?? "--" },
                  {
                    label: sessionsHeaderLabel,
                    value: r.frSessions ?? "--",
                    hint: r.triedOnSub,
                  },
                ]}
              />
            )}
            renderDetails={(r) => (
              <RecordFields
                fields={[
                  {
                    label: t("storeComparison.fields.unitsPerSession"),
                    value: r.unitsPerSession ?? "--",
                    hint: r.upsSub,
                  },
                  {
                    label: t("storeComparison.fields.missedRequests"),
                    value: r.missedPct ?? "--",
                    hint: r.missedSub,
                  },
                  {
                    label: t("storeComparison.fields.averageResponseTime"),
                    value: r.avgResponseTime ?? "--",
                    hint: r.avgResponseGoalSub,
                  },
                  {
                    label: t("storeComparison.fields.namedShoppers"),
                    value: r.namedShoppers ?? "--",
                  },
                  { label: t("storeComparison.fields.dwellTime"), value: r.dwellTime ?? "--" },
                  {
                    label: t("storeComparison.fields.experienceRating"),
                    value: renderStarRating(r.customerSatisfaction),
                    hint: r.customerSatisfactionSub,
                  },
                ]}
              />
            )}
            emptyState={
              <AdminEmptyState
                icon={<SearchOffIcon sx={{ fontSize: 40 }} />}
                text={t("storeComparison.empty.noDataForSelection")}
              />
            }
          />
        </ComparisonBreakdownPanel>
      </>
    </div>
  );
}
